// 棚卸しの履歴を【まとめて】取り込む。
//
// なぜ共通の CsvImportPanel を使わないか＝この系統だけ、分かる場合は**ファイルごとに
// 実施日・拠点を補足**する（Stocky の書き出しにも画面にも無い場合は不明のまま）。
// 共通パネルは1ファイル前提で「preview → 対応付け → dry-run →
// 適用」を1本の状態機械として持っており、そこへ N 件ぶんの日付・進行・結果を
// 差し込むと、共通パネルを使う他の系統ぜんぶに分岐が増える。**列の対応付けだけ
// 共通部品を借りて、進行はここで持つ。**
//
// 実採取した Stocky export は固有ヘッダで認識し、Shopify ID / Adjustment /
// Current Stock を固定対応する。ファイル名と列順には依存しない。未知形式だけは
// 明示的な詳細設定を開いた場合に限り、列の対応付けを全ファイルへ使い回す。
//
// 🔴 書込み前に全ファイルを dry-run する。履歴は既存を上書きしないが、未一致・
// 無効・数量なしの行を除外したまま記録すると不完全な監査履歴になる。ファイル別の
// 内訳を先に見せ、別ボタンで明示確認してから実適用する。0件は適用対象にしない。
//
// 🔴 部分適用を巻き戻さない。棚卸しは互いに独立した記録で、3件目が失敗しても
// 1・2件目を消す理由が無い（消す方が取り込めたはずの履歴を失う）。失敗した
// ファイルだけ「未取り込み」として残り、そのまま押し直せる。
import {
  Badge,
  Banner,
  BlockStack,
  Button,
  Card,
  Checkbox,
  ChoiceList,
  InlineStack,
  Select,
  Text,
  TextField,
} from "@shopify/polaris";
import { useQuery } from "@tanstack/react-query";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { ApiError } from "../../api/client";
import { listStocktakes } from "../../api/stocktakes";
import {
  fileToBase64,
  listStocktakeHistoryLocationOptions,
  type StocktakeHistoryCsvResult,
  type StockyStocktakeListMetadataInput,
} from "../../api/stocky_csv_import";
import { MappingSelects } from "../../components/CsvImportPanel";
import { QueryErrorBanner } from "../../components/QueryErrorBanner";
import {
  csvErrorMessage,
  initialMapping,
  isMappingReady,
  toPayloadMapping,
} from "../../components/csvImportSpec";
import { useAuthoritativeShop } from "../../hooks/useAuthoritativeShop";
import { t } from "../../lib/i18n";
import { buildStocktakeHistorySpec } from "./specs";
import {
  parseStockyStocktakeList,
  type StockyStocktakeListMetadata,
  type StockyStocktakeListParseError,
} from "./stockyStocktakeList";

/** 一度に選べるファイル数の上限。順に適用するので、多すぎると待ち時間が読めない。 */
const MAX_FILES = 100;
// base64化で約4/3に増え、preview応答と同時にstateへ残る。
// 単一ファイル・選択したバッチ合計ともに25MiBを上限にする。
const MAX_TOTAL_FILE_BYTES = 25 * 1024 * 1024;
// 大容量body middlewareはレスポンス送信後のfinallyで受信/実行leaseを解放する。
// 直前のpreviewや別処理と入れ違った429だけは、backendのRetry-After契約と同じ
// 5秒を待って同じファイルを1回だけ再送する。2回目も429なら行に理由を残す。
const PREVIEW_BUSY_RETRY_MS = 5_000;
const PREVIEW_MAX_ATTEMPTS = 2;

/** 固有ヘッダーだけ自動対応し、Current Stock の意味はファイルごとに人が確定する。 */
const STOCKY_BASE_MAPPING: Record<string, string> = {
  shopify_variant_id: "Shopify ID",
  sku: "SKU",
  product_title: "Product",
  variant_title: "Variant",
  difference: "",
  counted_qty: "",
  expected_qty: "",
};

type ImportMode = "none" | "stocky" | "manual";
type CurrentStockMeaning = "" | "unknown" | "counted" | "expected";

function stockyMapping(meaning: CurrentStockMeaning): Record<string, string> {
  return {
    ...STOCKY_BASE_MAPPING,
    difference: meaning === "unknown" || meaning === "" ? "" : "Adjustment",
    counted_qty: meaning === "counted" ? "Current Stock" : "",
    expected_qty: meaning === "expected" ? "Current Stock" : "",
  };
}

type RowStatus =
  | "pending"
  | "checking"
  | "ready"
  | "ready_partial"
  | "ready_duplicate"
  | "applying"
  | "done"
  | "partial"
  | "done_duplicate"
  | "already"
  | "unrecordable"
  | "failed"
  | "invalid";

interface FileRow {
  /** React の key。同名ファイルを複数選べるので添字を混ぜる。 */
  readonly key: string;
  readonly name: string;
  readonly mediaType: string;
  readonly base64: string;
  readonly headers: readonly string[];
  readonly totalRows: number;
  /** 1回の選択からdry-run/apply/通信再試行まで変えない。 */
  readonly sourceImportId: string;
  /** 同じbytesを別の棚卸しとして保存する場合だけ、dry-run後に人が明示確認する。 */
  readonly duplicateSourceConfirmed: boolean;
  /** 空文字は「不明」。後から履歴詳細で補足できる。 */
  readonly countedOn: string;
  /** 空文字は「不明」。現在の主拠点などを推測して入れない。 */
  readonly locationId: string;
  /** CSVだけでは確定できないため、ファイルごとに初期値なしで選ぶ。 */
  readonly currentStockMeaning: CurrentStockMeaning;
  /** CSVにはStocky No.が無いので、一覧との対応は別に保持する。 */
  readonly stockyListMetadataId: string;
  readonly stockyListAssociationMethod:
    "single_file_single_row" | "merchant_selected" | null;
  /** Generated at は実施日ではない。明示確認時だけ countedOn に写す。 */
  readonly useGeneratedDateAsCountedOn: boolean;
  /** 一覧行または「対応しない」を利用者（1×1は自動）が確定したか。 */
  readonly stockyListSelectionConfirmed: boolean;
  readonly status: RowStatus;
  /** 直近の dry-run。mapping/date/note の編集時に必ず破棄する。 */
  readonly dryRunResult: StocktakeHistoryCsvResult | null;
  /** 失敗の理由・取り込み結果の内訳。 */
  readonly message: string | null;
}

type PreviewOutcome<T> =
  | { readonly kind: "success"; readonly value: T }
  | { readonly kind: "cancelled" }
  | { readonly kind: "error"; readonly error: unknown };

/** generation切替やunmountで待機を即座に解き、旧shopから次のPOSTを送らない。 */
function waitForPreviewRetry(signal: AbortSignal): Promise<boolean> {
  if (signal.aborted) return Promise.resolve(false);
  return new Promise((resolve) => {
    let settled = false;
    const finish = (canRetry: boolean) => {
      if (settled) return;
      settled = true;
      window.clearTimeout(timeout);
      signal.removeEventListener("abort", cancel);
      resolve(canRetry);
    };
    const cancel = () => finish(false);
    const timeout = window.setTimeout(
      () => finish(!signal.aborted),
      PREVIEW_BUSY_RETRY_MS,
    );
    signal.addEventListener("abort", cancel, { once: true });
    // abortが先頭の確認とlistener登録の間に入っても5秒待たせない。
    if (signal.aborted) cancel();
  });
}

/**
 * 1ファイルのpreviewを完了させてからだけ次へ進めるworker用。
 * 429は黙殺せず1回だけexact retryし、なお失敗なら元のApiErrorを行へ返す。
 */
async function previewWithBusyRetry<T>(
  preview: () => Promise<T>,
  signal: AbortSignal,
  isCurrent: () => boolean,
): Promise<PreviewOutcome<T>> {
  for (let attempt = 0; attempt < PREVIEW_MAX_ATTEMPTS; attempt += 1) {
    if (signal.aborted || !isCurrent()) return { kind: "cancelled" };
    try {
      const value = await preview();
      return signal.aborted || !isCurrent()
        ? { kind: "cancelled" }
        : { kind: "success", value };
    } catch (error) {
      if (signal.aborted || !isCurrent()) return { kind: "cancelled" };
      const canRetry =
        error instanceof ApiError &&
        error.status === 429 &&
        attempt + 1 < PREVIEW_MAX_ATTEMPTS;
      if (!canRetry) return { kind: "error", error };
      if (!(await waitForPreviewRetry(signal)) || !isCurrent()) {
        return { kind: "cancelled" };
      }
    }
  }
  return { kind: "cancelled" };
}

/** UUIDはpreview再試行で作り直さず、dry-runから確定までファイルごとに1つ。 */
function initialFileRow(file: File, index: number): FileRow {
  return {
    key: `${index}-${file.name}`,
    name: file.name,
    mediaType: file.type || "text/csv",
    base64: "",
    headers: [],
    totalRows: 0,
    sourceImportId: globalThis.crypto.randomUUID(),
    duplicateSourceConfirmed: false,
    countedOn: "",
    locationId: "",
    currentStockMeaning: "",
    stockyListMetadataId: "",
    stockyListAssociationMethod: null,
    useGeneratedDateAsCountedOn: false,
    stockyListSelectionConfirmed: false,
    status: "pending",
    dryRunResult: null,
    message: null,
  };
}

function metadataInput(
  row: FileRow,
  metadata: StockyStocktakeListMetadata | null,
): StockyStocktakeListMetadataInput | null {
  if (metadata === null || row.stockyListAssociationMethod === null)
    return null;
  return {
    stocky_number: metadata.stockyNumber,
    name: metadata.name,
    location_name: metadata.locationName,
    generated_at_raw: metadata.generatedAtRaw,
    status: metadata.status,
    submitted_header_text: metadata.submittedHeaderText,
    submitted_row_text: metadata.submittedRowText,
    association_method: row.stockyListAssociationMethod,
  };
}

function resultAcknowledgesMetadata(
  result: StocktakeHistoryCsvResult,
  expected: StockyStocktakeListMetadataInput | null,
): boolean {
  if (expected === null) return true;
  const actual = result.stocky_stocktake_list;
  return (
    actual?.schema_version === 1 &&
    actual.kind === "stocky_stocktake_list_row" &&
    actual.association_method === expected.association_method &&
    actual.csv_contains_stocky_identifier === false &&
    actual.number_raw === expected.stocky_number &&
    actual.name_raw === expected.name &&
    actual.location_raw === expected.location_name &&
    actual.generated_at.raw === expected.generated_at_raw &&
    actual.generated_at.meaning === "stocky_generated_at" &&
    actual.generated_at.timezone === null &&
    actual.status_raw === expected.status &&
    actual.submitted_header_text === expected.submitted_header_text &&
    actual.submitted_row_text === expected.submitted_row_text
  );
}

function metadataParseErrorMessage(
  error: StockyStocktakeListParseError,
): string {
  const messages: Record<StockyStocktakeListParseError, string> = {
    too_large:
      "貼り付けた一覧が大きすぎます。必要な棚卸しを含む範囲だけを貼り付けてください。",
    header_not_found:
      "見出しを確認できません。No.・Name・Location・Generated at・Status を含めて表をコピーしてください。",
    duplicate_header:
      "同じ意味の見出しが複数あります。Stocky の棚卸し一覧だけをコピーしてください。",
    duplicate_stocky_number:
      "同じ Stocky No. が複数あります。重複行を除いてから貼り付けてください。",
    too_many_rows:
      "一覧が10,000件を超えています。今回のCSVに対応する範囲だけを貼り付けてください。",
    no_data_rows:
      "見出しは確認できましたが、No. と Generated at がある棚卸し行を読み取れませんでした。",
  };
  return t(messages[error]);
}

/** 取り込みの対象になり得る行＝未取り込みと、失敗して押し直せる行。 */
function isPending(row: FileRow): boolean {
  return row.status === "pending" || row.status === "failed";
}

function isReady(row: FileRow): boolean {
  return (
    (row.status === "ready" ||
      row.status === "ready_partial" ||
      row.status === "ready_duplicate") &&
    row.dryRunResult?.dry_run === true &&
    (row.dryRunResult.recorded_items > 0 ||
      isRetentionOnlyResult(row.dryRunResult)) &&
    typeof row.dryRunResult.plan_token === "string" &&
    row.dryRunResult.plan_token.length > 0 &&
    (row.dryRunResult.same_source_file_imports === 0 ||
      row.duplicateSourceConfirmed)
  );
}

function needsDuplicateSourceConfirmation(row: FileRow): boolean {
  return (
    row.dryRunResult?.dry_run === true &&
    !row.dryRunResult.already_imported &&
    row.dryRunResult.same_source_file_imports > 0 &&
    !row.duplicateSourceConfirmed
  );
}

function isRetentionOnlyResult(result: StocktakeHistoryCsvResult): boolean {
  return (
    result.normalization_mode === "retention_only" &&
    result.normalized_rows === 0 &&
    result.unprocessed_rows === result.total_rows &&
    result.domain_normalization_complete === false &&
    (result.retention_reason === "hard_row_cap_exceeded" ||
      result.retention_reason === "recognized_empty_source")
  );
}

function canInvalidate(row: FileRow): boolean {
  return (
    row.status === "pending" ||
    row.status === "checking" ||
    row.status === "ready" ||
    row.status === "ready_partial" ||
    row.status === "ready_duplicate" ||
    row.status === "failed" ||
    row.status === "unrecordable"
  );
}

function resetValidation(row: FileRow): FileRow {
  if (!canInvalidate(row)) return row;
  return {
    ...row,
    status: "pending",
    duplicateSourceConfirmed: false,
    dryRunResult: null,
    message: null,
  };
}

function duplicateHeaders(headers: readonly string[]): readonly string[] {
  const counts = new Map<string, number>();
  for (const header of headers) {
    counts.set(header, (counts.get(header) ?? 0) + 1);
  }
  return [...counts.entries()]
    .filter(([, count]) => count > 1)
    .map(([header]) => header || t("（空の列名）"));
}

function sameHeaderSet(a: readonly string[], b: readonly string[]): boolean {
  return (
    a.length === b.length &&
    duplicateHeaders(a).length === 0 &&
    duplicateHeaders(b).length === 0 &&
    a.every((header) => b.includes(header))
  );
}

function hasUniqueSelectedColumns(mapping: Record<string, string>): boolean {
  const selected = Object.values(mapping).filter(Boolean);
  return new Set(selected).size === selected.length;
}

const STATUS_LABELS: Record<RowStatus, string> = {
  pending: "未確認",
  checking: "確認中",
  ready: "確認済み",
  ready_partial: "要確認",
  ready_duplicate: "同じCSVを確認してください",
  applying: "取り込み中",
  done: "取り込み済み",
  partial: "一部を取り込み済み",
  done_duplicate: "取り込み済み（同じCSVあり）",
  already: "すでに取り込み済み",
  unrecordable: "記録対象なし",
  failed: "失敗",
  invalid: "取り込めません",
};

function issueCounts(result: StocktakeHistoryCsvResult) {
  return {
    unmatched: result.unmatched_count ?? result.unmatched_keys.length,
    invalid: result.invalid_row_count ?? result.invalid_rows.length,
    skipped: result.skipped_rows,
  };
}

function hasExcludedRows(result: StocktakeHistoryCsvResult): boolean {
  const counts = issueCounts(result);
  return (
    counts.unmatched > 0 ||
    counts.invalid > 0 ||
    counts.skipped > 0 ||
    result.source_only_items > 0 ||
    result.source_incomplete_items > 0
  );
}

function readyStatus(result: StocktakeHistoryCsvResult): RowStatus {
  if (isRetentionOnlyResult(result) || hasExcludedRows(result)) {
    return "ready_partial";
  }
  return result.same_source_file_imports > 0 ? "ready_duplicate" : "ready";
}

function completedStatus(result: StocktakeHistoryCsvResult): RowStatus {
  if (hasExcludedRows(result)) return "partial";
  return result.same_source_file_imports > 0 ? "done_duplicate" : "done";
}

/** 件数だけでなく、API が返した未一致キーと無効理由もファイル行に残す。 */
function resultMessage(result: StocktakeHistoryCsvResult): string {
  if (isRetentionOnlyResult(result)) {
    return result.already_imported
      ? t(
          "このCSVの原本 {rows} 行は完全保存済みです。棚卸し履歴や現在在庫には書き込んでいません。",
          { rows: result.unprocessed_rows ?? result.total_rows },
        )
      : result.retention_reason === "recognized_empty_source"
        ? t(
            "Stockyの対象データが0件だったため、ヘッダーを含む原本を完全保存しました。棚卸し履歴や現在在庫には書き込んでいません。",
          )
        : t(
            "10万物理行を超えたため、原本 {rows} 行を完全保存し、棚卸し履歴や現在在庫には書き込んでいません。保存済みデータの取り出しから全行を汎用形式で分割取得できます。",
            { rows: result.unprocessed_rows ?? result.total_rows },
          );
  }
  if (result.already_imported) {
    return t(
      "この CSV は棚卸し #{id} として取り込み済みです。重複して作成していません。",
      {
        id: result.stocktake_id ?? "-",
      },
    );
  }
  const counts = issueCounts(result);
  const messages = [
    t(
      "記録 {recorded} 件・現行商品一致 {matched} 件・原本IDのみ {sourceOnly} 件・原文数量が片方のみ {sourceIncomplete} 件・数量なし {skipped} 件・商品未一致 {unmatched} 件・無効 {invalid} 件",
      {
        recorded: result.recorded_items,
        matched: result.matched_current_items,
        sourceOnly: result.source_only_items,
        sourceIncomplete: result.source_incomplete_items,
        skipped: counts.skipped,
        unmatched: counts.unmatched,
        invalid: counts.invalid,
      },
    ),
  ];
  if (result.recorded_items === 0) {
    messages.unshift(t("記録できる明細が0件のため、棚卸しは作成していません"));
  }
  if (result.same_source_file_imports > 0) {
    messages.push(
      t(
        "同じ内容のCSVを使った履歴が既に {count} 件あります。別の棚卸しならそのまま取り込めます。通信再試行は自動で重複を防ぎます。",
        { count: result.same_source_file_imports },
      ),
    );
  }
  if (counts.unmatched > 0 && result.unmatched_keys.length > 0) {
    messages.push(
      t("未一致の商品: {keys}", {
        keys: result.unmatched_keys.slice(0, 5).join(", "),
      }),
    );
  }
  if (counts.invalid > 0 && result.invalid_rows.length > 0) {
    messages.push(
      t("無効な行: {rows}", {
        rows: result.invalid_rows
          .slice(0, 5)
          .map((row) =>
            t("{row}行目（{reason}）", { row: row.row, reason: row.reason }),
          )
          .join(" / "),
      }),
    );
  }
  return messages.join(" / ");
}

interface Props {
  /** 取り込みが1件でも成功したら呼ばれる（親が関連クエリを invalidate する）。 */
  onImported: () => void;
}

export function StocktakeHistoryImport(props: Props) {
  const authoritativeShop = useAuthoritativeShop();

  if (authoritativeShop.isError) {
    return (
      <QueryErrorBanner
        title={t("ストア情報を取得できませんでした")}
        onRetry={authoritativeShop.retry}
      />
    );
  }
  if (authoritativeShop.shopGeneration === null) {
    return <Text as="p">{t("ストア情報を確認しています…")}</Text>;
  }

  return (
    <StocktakeHistoryImportSession
      key={authoritativeShop.shopGeneration}
      {...props}
      shopGeneration={authoritativeShop.shopGeneration}
    />
  );
}

function StocktakeHistoryImportSession({
  onImported,
  shopGeneration,
}: Props & { shopGeneration: string }) {
  const [rows, setRows] = useState<readonly FileRow[]>([]);
  const [headers, setHeaders] = useState<readonly string[]>([]);
  const [mapping, setMapping] = useState<Record<string, string>>({});
  const [importMode, setImportMode] = useState<ImportMode>("none");
  const [isAdvancedOpen, setIsAdvancedOpen] = useState(false);
  const [isStockyListOpen, setIsStockyListOpen] = useState(false);
  const [stockyListText, setStockyListText] = useState("");
  const [note, setNote] = useState("");
  const [isReading, setIsReading] = useState(false);
  const [isChecking, setIsChecking] = useState(false);
  const [isApplying, setIsApplying] = useState(false);
  const [fileError, setFileError] = useState<string | null>(null);
  // 編集中に古い dry-run 応答が戻っても確認済みにしないための generation。
  const validationGeneration = useRef(0);
  const sessionActive = useRef(true);
  const previewRunAbort = useRef<AbortController | null>(null);
  const stockyListParse = useMemo(
    () =>
      stockyListText.trim()
        ? parseStockyStocktakeList(stockyListText)
        : { rows: [], error: null, skippedRows: 0, invalidDateRows: 0 },
    [stockyListText],
  );
  const stockyListRows = stockyListParse.rows;
  const stockyListById = useMemo(
    () => new Map(stockyListRows.map((row) => [row.id, row])),
    [stockyListRows],
  );

  useEffect(() => {
    // React StrictMode の setup→cleanup→setup でも現行 session を有効に戻す。
    sessionActive.current = true;
    return () => {
      sessionActive.current = false;
      validationGeneration.current += 1;
      previewRunAbort.current?.abort();
      previewRunAbort.current = null;
    };
  }, []);

  // 列の定義・案内文・エンドポイントは共通の spec から借りる。実施日は行ごとに
  // 違うので、ここで作る spec は「対応付けの UI 用」＝適用時に作り直す。
  const uiSpec = useMemo(
    () =>
      buildStocktakeHistorySpec({
        countedOn: null,
        locationId: null,
        note: "",
        sourceImportId: "",
        sourceFileName: "",
        sourceMediaType: "text/csv",
        sourceQuantityInterpretation: null,
        stockyListMetadata: null,
      }),
    [],
  );

  // 既に登録済みの棚卸しの日付＝二重登録の注意に使う。Stocky取込は timezone
  // 変換しない明示 DATE が権威値。通常棚卸しだけ started_at の日付へfallbackする。
  const { data: existingStocktakes } = useQuery({
    queryKey: ["stocktakes", "shop-generation", shopGeneration],
    queryFn: listStocktakes,
  });
  const locationsQuery = useQuery({
    queryKey: [
      "stocktake-history-location-options",
      "shop-generation",
      shopGeneration,
    ],
    queryFn: listStocktakeHistoryLocationOptions,
  });
  const existingDates = useMemo(
    () =>
      new Set(
        (existingStocktakes ?? []).flatMap((s) => {
          if (s.source_import_id) {
            return s.source_counted_on ? [s.source_counted_on] : [];
          }
          return [s.started_at.slice(0, 10)];
        }),
      ),
    [existingStocktakes],
  );

  const handleFilesChange = useCallback(
    async (event: React.ChangeEvent<HTMLInputElement>) => {
      const files = Array.from(event.target.files ?? []);
      if (files.length === 0) return;
      previewRunAbort.current?.abort();
      const generation = validationGeneration.current + 1;
      validationGeneration.current = generation;
      // 新しい選択をした時点で、旧ファイルの dry-run readiness は破棄する。
      setRows([]);
      setHeaders([]);
      setMapping({});
      setImportMode("none");
      setIsAdvancedOpen(false);
      if (files.length > MAX_FILES) {
        setIsReading(false);
        setFileError(
          t(
            "一度に選べるのは {max} ファイルまでです。分けて取り込んでください。",
            {
              max: MAX_FILES,
            },
          ),
        );
        return;
      }
      const totalBytes = files.reduce((sum, file) => sum + file.size, 0);
      if (totalBytes > MAX_TOTAL_FILE_BYTES) {
        setIsReading(false);
        setFileError(
          t(
            "選んだCSVの合計サイズが大きすぎます（上限 25MB）。複数回に分けて取り込んでください。",
          ),
        );
        return;
      }
      setIsReading(true);
      setFileError(null);
      const abortController = new AbortController();
      previewRunAbort.current = abortController;
      const isCurrent = () =>
        sessionActive.current &&
        validationGeneration.current === generation &&
        previewRunAbort.current === abortController &&
        !abortController.signal.aborted;
      try {
        const previewed: Array<{ row: FileRow; isKnownStocky: boolean }> = [];
        // 🔴 1 workerで選択順に処理する。base64化だけでなくpreview POSTも前の
        // ファイルが成功/失敗するまで次を開始しない（host-heavy admission契約）。
        for (const [index, file] of files.entries()) {
          if (!isCurrent()) return;
          const initial = initialFileRow(file, index);
          try {
            const base64 = await fileToBase64(file);
            if (!isCurrent()) return;
            const outcome = await previewWithBusyRetry(
              () => uiSpec.preview(base64),
              abortController.signal,
              isCurrent,
            );
            if (outcome.kind === "cancelled") return;
            if (outcome.kind === "error") throw outcome.error;
            const preview = outcome.value;
            if (!isCurrent()) return;
            previewed.push({
              row: {
                ...initial,
                base64,
                headers: preview.headers,
                totalRows: preview.total_rows,
              },
              isKnownStocky:
                preview.detected_format === "stocky_stocktake" &&
                duplicateHeaders(preview.headers).length === 0,
            });
          } catch (error) {
            if (!isCurrent()) return;
            previewed.push({
              row: {
                ...initial,
                status: "invalid",
                message: csvErrorMessage(error),
              },
              isKnownStocky: false,
            });
          }
        }

        if (!isCurrent()) return;

        const first = previewed.find(
          (entry) =>
            entry.row.status === "pending" &&
            duplicateHeaders(entry.row.headers).length === 0,
        );
        if (first === undefined) {
          setRows(
            previewed.map((entry) => {
              const duplicates = duplicateHeaders(entry.row.headers);
              return entry.row.status === "pending" && duplicates.length > 0
                ? {
                    ...entry.row,
                    status: "invalid" as RowStatus,
                    message: t(
                      "同じ列名が複数あります。列名を一意にしてから再度アップロードしてください: {columns}",
                      { columns: duplicates.join(", ") },
                    ),
                  }
                : entry.row;
            }),
          );
          setHeaders([]);
          return;
        }
        const mode: ImportMode = first.isKnownStocky ? "stocky" : "manual";
        // Stocky 固有署名なら列順・追加列の違いを許す。未知形式は列名集合が同じ
        // ファイルだけに限定し、意味を推測した対応付けを黙って共有しない。
        setRows(
          previewed.map((entry) => {
            if (entry.row.status !== "pending") return entry.row;
            const duplicates = duplicateHeaders(entry.row.headers);
            if (duplicates.length > 0) {
              return {
                ...entry.row,
                status: "invalid" as RowStatus,
                message: t(
                  "同じ列名が複数あります。列名を一意にしてから再度アップロードしてください: {columns}",
                  { columns: duplicates.join(", ") },
                ),
              };
            }
            const compatible =
              mode === "stocky"
                ? entry.isKnownStocky
                : !entry.isKnownStocky &&
                  sameHeaderSet(entry.row.headers, first.row.headers);
            return compatible
              ? entry.row
              : {
                  ...entry.row,
                  status: "invalid" as RowStatus,
                  message: t(
                    "列の構成が他のファイルと違います。同じ書き出し画面の CSV だけをまとめて選んでください。",
                  ),
                };
          }),
        );
        setHeaders(first.row.headers);
        setImportMode(mode);
        // 未知形式では guessed_mapping を一切採用しない。詳細設定を開いた人が
        // 自分で選ぶまで空のまま＝似た列名から数量の意味を推測しない。
        setMapping(
          mode === "stocky" ? STOCKY_BASE_MAPPING : initialMapping(uiSpec, {}),
        );
      } finally {
        if (previewRunAbort.current === abortController) {
          previewRunAbort.current = null;
        }
        if (
          sessionActive.current &&
          generation === validationGeneration.current
        ) {
          setIsReading(false);
        }
      }
    },
    [uiSpec],
  );

  const isBusy = isReading || isChecking || isApplying;
  const isStockyListLocked = rows.some(
    (row) =>
      row.status === "done" ||
      row.status === "partial" ||
      row.status === "done_duplicate" ||
      row.status === "already",
  );

  const updateStockyListText = useCallback(
    (value: string) => {
      if (isBusy || isStockyListLocked) return;
      validationGeneration.current += 1;
      setStockyListText(value);
      // 一覧を貼り直したら、旧行との対応とそれを根拠にした実施日を破棄する。
      setRows((current) =>
        current.map((row) => ({
          ...resetValidation(row),
          countedOn: row.useGeneratedDateAsCountedOn ? "" : row.countedOn,
          stockyListMetadataId: "",
          stockyListAssociationMethod: null,
          useGeneratedDateAsCountedOn: false,
          stockyListSelectionConfirmed: false,
        })),
      );
    },
    [isBusy, isStockyListLocked],
  );

  // CSVにも一覧にも1件しか無いときだけ自動対応する。ファイル順やファイル名の
  // (2) から複数件を推測することはしない。
  useEffect(() => {
    if (importMode !== "stocky" || stockyListRows.length !== 1) return;
    setRows((current) => {
      const candidates = current.filter((row) => row.status !== "invalid");
      if (candidates.length !== 1) return current;
      const target = candidates[0];
      if (
        !canInvalidate(target) ||
        target.stockyListMetadataId !== "" ||
        target.stockyListSelectionConfirmed
      ) {
        return current;
      }
      return current.map((row) =>
        row.key === target.key
          ? {
              ...resetValidation(row),
              stockyListMetadataId: stockyListRows[0].id,
              stockyListAssociationMethod: "single_file_single_row" as const,
              stockyListSelectionConfirmed: true,
            }
          : row,
      );
    });
  }, [importMode, stockyListRows]);

  const updateMapping = useCallback(
    (key: string, value: string) => {
      if (isBusy) return;
      validationGeneration.current += 1;
      setMapping((current) => ({ ...current, [key]: value }));
      setRows((current) => current.map(resetValidation));
    },
    [isBusy],
  );

  const updateNote = useCallback(
    (value: string) => {
      if (isBusy) return;
      validationGeneration.current += 1;
      setNote(value);
      setRows((current) => current.map(resetValidation));
    },
    [isBusy],
  );

  const updateCountedOn = useCallback(
    (key: string, value: string) => {
      if (isBusy) return;
      validationGeneration.current += 1;
      setRows((current) =>
        current.map((row) =>
          row.key === key
            ? {
                ...resetValidation(row),
                countedOn: value,
                useGeneratedDateAsCountedOn: false,
              }
            : row,
        ),
      );
    },
    [isBusy],
  );

  const updateLocationId = useCallback(
    (key: string, value: string) => {
      if (isBusy) return;
      validationGeneration.current += 1;
      setRows((current) =>
        current.map((row) =>
          row.key === key
            ? { ...resetValidation(row), locationId: value }
            : row,
        ),
      );
    },
    [isBusy],
  );

  const updateCurrentStockMeaning = useCallback(
    (key: string, value: CurrentStockMeaning) => {
      if (isBusy) return;
      validationGeneration.current += 1;
      setRows((current) =>
        current.map((row) =>
          row.key === key
            ? { ...resetValidation(row), currentStockMeaning: value }
            : row,
        ),
      );
    },
    [isBusy],
  );

  const updateDuplicateSourceConfirmation = useCallback(
    (key: string, checked: boolean) => {
      if (isBusy) return;
      setRows((current) =>
        current.map((row) =>
          row.key === key
            ? { ...row, duplicateSourceConfirmed: checked }
            : row,
        ),
      );
    },
    [isBusy],
  );

  const updateStockyListMetadata = useCallback(
    (key: string, metadataId: string) => {
      if (isBusy) return;
      validationGeneration.current += 1;
      setRows((current) =>
        current.map((row) => {
          if (row.key !== key) return row;
          return {
            ...resetValidation(row),
            countedOn: row.useGeneratedDateAsCountedOn ? "" : row.countedOn,
            stockyListMetadataId: metadataId,
            stockyListAssociationMethod:
              metadataId === "" ? null : "merchant_selected",
            useGeneratedDateAsCountedOn: false,
            stockyListSelectionConfirmed: true,
          };
        }),
      );
    },
    [isBusy],
  );

  const updateUseGeneratedDate = useCallback(
    (key: string, checked: boolean) => {
      if (isBusy) return;
      validationGeneration.current += 1;
      setRows((current) =>
        current.map((row) => {
          if (row.key !== key) return row;
          const metadata = stockyListById.get(row.stockyListMetadataId);
          if (!metadata?.generatedOn) return row;
          return {
            ...resetValidation(row),
            countedOn: checked
              ? metadata.generatedOn
              : row.useGeneratedDateAsCountedOn
                ? ""
                : row.countedOn,
            useGeneratedDateAsCountedOn: checked,
          };
        }),
      );
    },
    [isBusy, stockyListById],
  );

  const pendingRows = rows.filter(isPending);
  const readyRows = rows.filter(isReady);
  const duplicateConfirmationRows = rows.filter(
    needsDuplicateSourceConfirmation,
  );
  const readyPartialCount = readyRows.filter(
    (row) => row.status === "ready_partial",
  ).length;
  const retentionReadyCount = readyRows.filter(
    (row) =>
      row.dryRunResult !== null && isRetentionOnlyResult(row.dryRunResult),
  ).length;
  const isMappingDone =
    headers.length > 0 &&
    (importMode === "stocky" ||
      (importMode === "manual" &&
        isAdvancedOpen &&
        isMappingReady(uiSpec, mapping) &&
        hasUniqueSelectedColumns(mapping)));
  const hasEveryCurrentStockMeaning =
    importMode !== "stocky" ||
    pendingRows.every((row) => row.currentStockMeaning !== "");
  const hasEveryStockyListChoice =
    importMode !== "stocky" ||
    stockyListRows.length === 0 ||
    pendingRows.every((row) => row.stockyListSelectionConfirmed);
  const hasValidStockyListPaste =
    stockyListText.trim() === "" || stockyListParse.error === null;
  const duplicateMetadataIds = new Set(
    rows
      .filter(
        (row) => row.stockyListMetadataId !== "" && row.status !== "invalid",
      )
      .map((row) => row.stockyListMetadataId)
      .filter(
        (id, _index, values) => values.indexOf(id) !== values.lastIndexOf(id),
      ),
  );
  const canCheck =
    isMappingDone &&
    pendingRows.length > 0 &&
    hasEveryCurrentStockMeaning &&
    hasEveryStockyListChoice &&
    hasValidStockyListPaste &&
    duplicateMetadataIds.size === 0 &&
    !isBusy;
  const canApply =
    readyRows.length > 0 &&
    pendingRows.length === 0 &&
    duplicateConfirmationRows.length === 0 &&
    !isBusy;

  const validateAll = useCallback(async () => {
    const targets = rows.filter(isPending);
    const generation = validationGeneration.current + 1;
    validationGeneration.current = generation;
    setIsChecking(true);
    try {
      for (const target of targets) {
        setRows((current) =>
          current.map((row) =>
            row.key === target.key
              ? {
                  ...row,
                  status: "checking",
                  dryRunResult: null,
                  message: null,
                }
              : row,
          ),
        );
        const selectedMetadata =
          stockyListById.get(target.stockyListMetadataId) ?? null;
        const requestedMetadata = metadataInput(target, selectedMetadata);
        const spec = buildStocktakeHistorySpec({
          countedOn: target.countedOn || null,
          locationId: target.locationId || null,
          note,
          sourceImportId: target.sourceImportId,
          sourceFileName: target.name,
          sourceMediaType: target.mediaType,
          sourceQuantityInterpretation:
            importMode === "stocky" && target.currentStockMeaning !== ""
              ? target.currentStockMeaning
              : null,
          stockyListMetadata: requestedMetadata,
        });
        try {
          const targetMapping =
            importMode === "stocky"
              ? stockyMapping(target.currentStockMeaning)
              : mapping;
          const result = await spec.apply(
            target.base64,
            toPayloadMapping(spec, targetMapping),
            {
              dryRun: true,
              sourceImportId: target.sourceImportId,
              sourceFileName: target.name,
              sourceMediaType: target.mediaType,
            },
          );
          if (validationGeneration.current !== generation) return;
          if (!resultAcknowledgesMetadata(result, requestedMetadata)) {
            throw new Error(
              t(
                "Stocky一覧の情報をサーバーが保存対象として確認できませんでした。画面を再読み込みしてから、もう一度お試しください。",
              ),
            );
          }
          if (
            result.normalization_mode === "retention_only" &&
            !isRetentionOnlyResult(result)
          ) {
            throw new Error(
              t(
                "運用DBへ変換せず原本と汎用形式を保持する確認結果の件数が一致しません。再度お試しください。",
              ),
            );
          }
          if (!sessionActive.current) return;
          const retentionOnly = isRetentionOnlyResult(result);
          const status: RowStatus = result.already_imported
            ? "already"
            : retentionOnly
              ? "ready_partial"
              : result.recorded_items === 0
                ? "unrecordable"
                : readyStatus(result);
          setRows((current) =>
            current.map((row) =>
              row.key === target.key
                ? {
                    ...row,
                    status,
                    duplicateSourceConfirmed: false,
                    dryRunResult: result,
                    message: resultMessage(result),
                  }
                : row,
            ),
          );
        } catch (error) {
          if (!sessionActive.current) return;
          if (validationGeneration.current !== generation) return;
          setRows((current) =>
            current.map((row) =>
              row.key === target.key
                ? {
                    ...row,
                    status: "failed",
                    dryRunResult: null,
                    message: csvErrorMessage(error),
                  }
                : row,
            ),
          );
        }
      }
    } finally {
      if (sessionActive.current) setIsChecking(false);
    }
  }, [rows, mapping, note, importMode, stockyListById]);

  const applyAll = useCallback(async () => {
    const targets = rows.filter(isReady);
    setIsApplying(true);
    let completedOccurrences = 0;
    try {
      for (const target of targets) {
        setRows((current) =>
          current.map((row) =>
            row.key === target.key
              ? { ...row, status: "applying", message: null }
              : row,
          ),
        );
        // 実施日は行ごとに違う＝spec をその場で作る（apply の中身は共通）。
        const selectedMetadata =
          stockyListById.get(target.stockyListMetadataId) ?? null;
        const requestedMetadata = metadataInput(target, selectedMetadata);
        const spec = buildStocktakeHistorySpec({
          countedOn: target.countedOn || null,
          locationId: target.locationId || null,
          note,
          sourceImportId: target.sourceImportId,
          sourceFileName: target.name,
          sourceMediaType: target.mediaType,
          sourceQuantityInterpretation:
            importMode === "stocky" && target.currentStockMeaning !== ""
              ? target.currentStockMeaning
              : null,
          stockyListMetadata: requestedMetadata,
        });
        try {
          const targetMapping =
            importMode === "stocky"
              ? stockyMapping(target.currentStockMeaning)
              : mapping;
          const result = await spec.apply(
            target.base64,
            toPayloadMapping(spec, targetMapping),
            {
              dryRun: false,
              expectedPlanToken: target.dryRunResult?.plan_token ?? undefined,
              ...(target.dryRunResult !== null &&
              isRetentionOnlyResult(target.dryRunResult)
                ? { confirmRetentionOnly: true }
                : {}),
              sourceImportId: target.sourceImportId,
              sourceFileName: target.name,
              sourceMediaType: target.mediaType,
            },
          );
          if (!sessionActive.current) return;
          if (!resultAcknowledgesMetadata(result, requestedMetadata)) {
            throw new Error(
              t(
                "Stocky一覧の情報をサーバーが保存対象として確認できませんでした。画面を再読み込みしてから、もう一度お試しください。",
              ),
            );
          }
          const retentionOnly = isRetentionOnlyResult(result);
          const status: RowStatus = result.already_imported
            ? "already"
            : retentionOnly
              ? "done"
              : result.recorded_items === 0
                ? "unrecordable"
                : completedStatus(result);
          if (
            !result.already_imported &&
            (result.recorded_items > 0 || retentionOnly)
          ) {
            completedOccurrences += 1;
          }
          setRows((current) =>
            current.map((row) =>
              row.key === target.key
                ? {
                    ...row,
                    status,
                    dryRunResult: null,
                    message: resultMessage(result),
                  }
                : row,
            ),
          );
        } catch (error) {
          if (!sessionActive.current) return;
          // 🔴 ここで中断しない＝残りのファイルは独立した記録で、
          //    1件の失敗に巻き込む理由が無い。
          // 応答だけ失われてサーバー側が確定済みの場合、exact retry には
          // 最初に使った確認tokenが必要。入力が変わらない限り破棄せず、同じ
          // 確認内容をそのまま再送できる状態へ戻す。
          const confirmed = target.dryRunResult;
          setRows((current) =>
            current.map((row) =>
              row.key === target.key
                ? {
                    ...row,
                    status:
                      confirmed && isReady(target)
                        ? readyStatus(confirmed)
                        : "failed",
                    dryRunResult: confirmed,
                    message: csvErrorMessage(error),
                  }
                : row,
            ),
          );
        }
      }
    } finally {
      if (sessionActive.current) setIsApplying(false);
    }
    if (sessionActive.current && completedOccurrences > 0) onImported();
  }, [rows, mapping, note, importMode, onImported, stockyListById]);

  const doneCount = rows.filter(
    (row) =>
      row.status === "done" ||
      row.status === "partial" ||
      row.status === "done_duplicate",
  ).length;
  const partialCount = rows.filter((row) => row.status === "partial").length;
  const duplicateDoneCount = rows.filter(
    (row) => row.status === "done_duplicate",
  ).length;
  const alreadyCount = rows.filter((row) => row.status === "already").length;
  const failedCount = rows.filter((row) => row.status === "failed").length;
  const invalidFileCount = rows.filter(
    (row) => row.status === "invalid",
  ).length;
  const unrecordableCount = rows.filter(
    (row) => row.status === "unrecordable",
  ).length;
  const hasUnconfirmedWrite =
    readyRows.length > 0 || duplicateConfirmationRows.length > 0 || isBusy;

  return (
    <section aria-label={t(uiSpec.title)}>
      <Card>
        <BlockStack gap="400">
          <BlockStack gap="100">
            <Text as="h2" variant="headingMd">
              {t(uiSpec.title)}
            </Text>
            <Text as="p" variant="bodySm" tone="subdued">
              {t(uiSpec.description)}
            </Text>
            {uiSpec.exportPath && (
              <Text as="p" variant="bodySm" tone="subdued">
                {t("書き出し方")}: {t(uiSpec.exportPath)}
              </Text>
            )}
          </BlockStack>

          <Text as="p" fontWeight="semibold">
            {t(
              "選ぶファイル: download_csv.csv（棚卸し1件につき1ファイル・複数選択可）",
            )}
          </Text>
          <input
            type="file"
            accept=".csv,text/csv"
            multiple
            id="csv-stocktakes-file"
            aria-label={t("棚卸しの CSV ファイル（複数選べます）")}
            disabled={isBusy}
            onChange={(event) => void handleFilesChange(event)}
          />
          {isReading && <Text as="p">{t("ファイルを読み込んでいます…")}</Text>}

          <BlockStack gap="200">
            <InlineStack align="start">
              <Button
                onClick={() => setIsStockyListOpen((current) => !current)}
                ariaExpanded={isStockyListOpen}
                disabled={isBusy}
              >
                {isStockyListOpen
                  ? t("Stocky の棚卸し一覧を閉じる")
                  : t(
                      "Stocky の棚卸し一覧から名称・作成日時・状態も取り込む（任意）",
                    )}
              </Button>
            </InlineStack>
            {isStockyListOpen && (
              <BlockStack gap="200">
                <Text as="p" variant="bodySm" tone="subdued">
                  {t(
                    "Stocky の Stocktakes 一覧で、No.・Name・Location・Generated at・Status を含む表を見出しごとコピーし、そのまま貼り付けてください。CSVが1件・一覧が1行なら自動で対応します。複数件では各CSVから正しい一覧行を選びます。",
                  )}
                </Text>
                <TextField
                  label={t("Stocky の棚卸し一覧")}
                  value={stockyListText}
                  onChange={updateStockyListText}
                  disabled={isBusy || isStockyListLocked}
                  autoComplete="off"
                  multiline={6}
                  placeholder={
                    "No.\tName\tType\tLocation\tGenerated at\tStatus\tAdjustment\tItems"
                  }
                />
                {stockyListParse.error && (
                  <Banner
                    tone="critical"
                    title={t("一覧を読み取れませんでした")}
                  >
                    <p>{metadataParseErrorMessage(stockyListParse.error)}</p>
                  </Banner>
                )}
                {!stockyListParse.error && stockyListRows.length > 0 && (
                  <Banner
                    tone={
                      stockyListParse.invalidDateRows > 0 ||
                      stockyListParse.skippedRows > 0
                        ? "warning"
                        : "success"
                    }
                    title={t("棚卸し一覧を {count} 件読み取りました", {
                      count: stockyListRows.length,
                    })}
                  >
                    <p>
                      {t(
                        "Generated at は Stocky で作成された日時として、タイムゾーンを付けずに正規化して保存します。棚卸しの完了日とは限らないため、実施日には自動設定しません。",
                      )}
                    </p>
                    {stockyListParse.invalidDateRows > 0 && (
                      <p>
                        {t(
                          "{count} 件の Generated at は形式を確認できないため、原文だけを保存します。",
                          { count: stockyListParse.invalidDateRows },
                        )}
                      </p>
                    )}
                    {stockyListParse.skippedRows > 0 && (
                      <p>
                        {t("表以外の {count} 行は対応対象から外しました。", {
                          count: stockyListParse.skippedRows,
                        })}
                      </p>
                    )}
                  </Banner>
                )}
                <Text as="p" variant="bodySm" tone="subdued">
                  {t(
                    "Stocky の一覧には独立したコメント欄がありません。Name は「Stockyでの棚卸し名」として別に保存し、下のメモへは自動でコピーしません。",
                  )}
                </Text>
              </BlockStack>
            )}
          </BlockStack>

          <TextField
            label={t("メモ（任意）")}
            helpText={t("選んだすべての棚卸しに同じメモが付きます。")}
            value={note}
            onChange={updateNote}
            disabled={isBusy}
            autoComplete="off"
            placeholder={t("例: Stocky から取り込み（本店）")}
          />

          {fileError && (
            <Banner tone="critical" title={t("ファイルを読み込めませんでした")}>
              <p>{fileError}</p>
            </Banner>
          )}

          {locationsQuery.isError && (
            <Banner tone="critical" title={t("拠点を取得できませんでした")}>
              <p>{t("画面を再読み込みしてから、もう一度お試しください。")}</p>
            </Banner>
          )}

          {headers.length > 0 && importMode === "stocky" && (
            <Banner
              tone="info"
              title={t("Stocky の棚卸し CSV を自動認識しました")}
            >
              <p>
                {t(
                  "Shopify ID・SKU・Product・Variant・Adjustment は自動で読み取ります。CSV には Current Stock が商品を数える前か後か書かれていないため、ファイルごとに下から選んでください。迷ったら「分からない（おすすめ）」を選べます。",
                )}
              </p>
            </Banner>
          )}

          {headers.length > 0 && importMode === "manual" && (
            <BlockStack gap="300">
              <Banner tone="warning" title={t("フォーマットが違います")}>
                <p>
                  {t(
                    "Stocky の棚卸し画面からダウンロードした download_csv.csv をアップロードしてください。ファイル名を変更していても同じ形式なら使えます。Stocky以外のCSVを使う場合だけ、詳細設定を開いて列の意味を指定してください。",
                  )}
                </p>
              </Banner>
              <InlineStack align="start">
                <Button
                  onClick={() => setIsAdvancedOpen((current) => !current)}
                  ariaExpanded={isAdvancedOpen}
                >
                  {isAdvancedOpen
                    ? t("詳細設定を閉じる")
                    : t("詳細設定（Stocky以外のCSV）を開く")}
                </Button>
              </InlineStack>
              {isAdvancedOpen && (
                <BlockStack gap="300">
                  <Text as="p">
                    {t(
                      "列の対応付けは選んだファイル全部に使われます（同じ書き出し画面の CSV なら列構成は同じです）。",
                    )}
                  </Text>
                  <MappingSelects
                    spec={uiSpec}
                    headers={[...headers]}
                    mapping={mapping}
                    onChange={updateMapping}
                  />
                  {!isMappingDone && (
                    <Text as="p" variant="bodySm" tone="subdued">
                      {t(uiSpec.readyHint)}
                    </Text>
                  )}
                  {!hasUniqueSelectedColumns(mapping) && (
                    <Text as="p" variant="bodySm" tone="critical">
                      {t(
                        "同じCSV列を複数の項目に使用できません。項目ごとに別の列を選んでください。",
                      )}
                    </Text>
                  )}
                </BlockStack>
              )}
            </BlockStack>
          )}

          {rows.length > 0 && (
            <BlockStack gap="300">
              <Text as="h3" variant="headingSm">
                {t("選んだファイル（{count} 件）", { count: rows.length })}
              </Text>
              {rows.map((row) => (
                <FileRowView
                  key={row.key}
                  row={row}
                  isDuplicateInBatch={
                    row.countedOn !== "" &&
                    rows.some(
                      (other) =>
                        other.key !== row.key &&
                        other.countedOn === row.countedOn &&
                        other.status !== "invalid",
                    )
                  }
                  isDuplicateOfExisting={
                    row.countedOn !== "" && existingDates.has(row.countedOn)
                  }
                  onCountedOnChange={updateCountedOn}
                  locationOptions={(locationsQuery.data ?? []).map(
                    (location) => ({
                      label: location.active
                        ? location.name
                        : t("{name}（無効）", { name: location.name }),
                      value: String(location.id),
                    }),
                  )}
                  onLocationIdChange={updateLocationId}
                  showCurrentStockMeaning={importMode === "stocky"}
                  onCurrentStockMeaningChange={updateCurrentStockMeaning}
                  onDuplicateSourceConfirmationChange={
                    updateDuplicateSourceConfirmation
                  }
                  showStockyListMetadata={importMode === "stocky"}
                  stockyListRows={stockyListRows}
                  selectedStockyListMetadataId={row.stockyListMetadataId}
                  stockyListSelectionConfirmed={
                    row.stockyListSelectionConfirmed
                  }
                  stockyListAssociationMethod={row.stockyListAssociationMethod}
                  onStockyListMetadataChange={updateStockyListMetadata}
                  useGeneratedDateAsCountedOn={row.useGeneratedDateAsCountedOn}
                  onUseGeneratedDateChange={updateUseGeneratedDate}
                  isDuplicateStockyListMetadata={duplicateMetadataIds.has(
                    row.stockyListMetadataId,
                  )}
                  disabled={isBusy}
                />
              ))}
            </BlockStack>
          )}

          {pendingRows.length > 0 && (
            <BlockStack gap="200">
              <Text as="p" variant="bodySm" tone="subdued">
                {t(
                  "実施日・拠点が分からないファイルも「不明」のまま取り込めます。元データから確認できたときに棚卸し履歴で後から設定してください。",
                )}
              </Text>
              {!hasEveryCurrentStockMeaning && (
                <Text as="p" variant="bodySm" tone="subdued">
                  {t(
                    "各ファイルで、Current Stock が商品を数える前か後かを選んでください。迷ったら「分からない（おすすめ）」を選べます。",
                  )}
                </Text>
              )}
              {duplicateMetadataIds.size > 0 && (
                <Text as="p" variant="bodySm" tone="critical">
                  {t(
                    "同じStocky一覧行を複数のCSVへ対応できません。各CSVで別の棚卸しを選んでください。",
                  )}
                </Text>
              )}
              <InlineStack align="end">
                <Button
                  variant="primary"
                  disabled={!canCheck}
                  loading={isChecking}
                  onClick={() => void validateAll()}
                >
                  {t("{count} 件の内容を確認する", {
                    count: pendingRows.length,
                  })}
                </Button>
              </InlineStack>
            </BlockStack>
          )}

          {readyRows.length > 0 && pendingRows.length === 0 && (
            <BlockStack gap="200">
              <Banner
                tone={
                  readyPartialCount > 0 || retentionReadyCount > 0
                    ? "warning"
                    : "info"
                }
                title={
                  retentionReadyCount > 0
                    ? t(
                        "{count} 件は棚卸し履歴へ書かず、原本と解析可能な全行の汎用形式を保持します",
                        { count: retentionReadyCount },
                      )
                    : readyPartialCount > 0
                      ? t(
                          "{count} 件に対象外の行があります。内訳を確認してから取り込んでください",
                          { count: readyPartialCount },
                        )
                      : t(
                          "事前確認が完了しました。内容を確認して取り込んでください",
                        )
                }
              >
                {retentionReadyCount > 0 ? (
                  <p>
                    {t(
                      "対象0件のStockyレポートはヘッダーを含む原本を完全保存します。10万物理行を超えるファイルも1行も捨てず、原本と解析可能な全行の汎用形式を保持しますが、この操作では棚卸し履歴や現在在庫を変更しません。保存後は「保存済みデータの取り出し」から全行を汎用形式で分割取得できます。下のボタンが、運用DBへ変換せず保持することの明示確認です。",
                    )}
                  </p>
                ) : (
                  <p>
                    {t(
                      "このボタンを押すまでは棚卸しを作成しません。取り込んでも Shopify の在庫は変わりません。",
                    )}
                  </p>
                )}
              </Banner>
              <InlineStack align="end">
                <Button
                  variant="primary"
                  disabled={!canApply}
                  loading={isApplying}
                  onClick={() => void applyAll()}
                >
                  {t("確認した内容で {count} 件を取り込む", {
                    count: readyRows.length,
                  })}
                </Button>
              </InlineStack>
            </BlockStack>
          )}

          {duplicateConfirmationRows.length > 0 && pendingRows.length === 0 && (
            <Banner
              tone="warning"
              title={t(
                "同じ内容のCSVを別の棚卸しとして保存するか確認してください",
              )}
            >
              <p>
                {t(
                  "通信再試行では重複しません。ファイルを選び直した今回の操作を別の棚卸しとして残す場合だけ、各ファイルの確認欄を選んでください。",
                )}
              </p>
            </Banner>
          )}

          {(doneCount > 0 ||
            alreadyCount > 0 ||
            failedCount > 0 ||
            invalidFileCount > 0 ||
            unrecordableCount > 0) &&
            !hasUnconfirmedWrite && (
              <Banner
                tone={
                  failedCount > 0 ||
                  invalidFileCount > 0 ||
                  unrecordableCount > 0 ||
                  partialCount > 0 ||
                  duplicateDoneCount > 0
                    ? "warning"
                    : doneCount > 0
                      ? "success"
                      : "info"
                }
                title={
                  failedCount > 0 ||
                  invalidFileCount > 0 ||
                  unrecordableCount > 0
                    ? t("{done} 件を取り込み、{failed} 件は失敗しました", {
                        done: doneCount,
                        failed:
                          failedCount + invalidFileCount + unrecordableCount,
                      })
                    : partialCount > 0
                      ? t("{done} 件を取り込みましたが、対象外の行があります", {
                          done: doneCount,
                        })
                      : duplicateDoneCount > 0
                        ? t(
                            "{done} 件を取り込みました。同じ内容のCSVが {duplicate} 件ありました",
                            {
                              done: doneCount,
                              duplicate: duplicateDoneCount,
                            },
                          )
                      : doneCount > 0 && alreadyCount > 0
                        ? t(
                            "{done} 件を取り込み、{already} 件は取り込み済みでした",
                            {
                              done: doneCount,
                              already: alreadyCount,
                            },
                          )
                        : alreadyCount > 0
                          ? t("選んだ {already} 件はすでに取り込み済みでした", {
                              already: alreadyCount,
                            })
                          : t(
                              "{done} 件の棚卸しを取り込みました（在庫は変わっていません）",
                              {
                                done: doneCount,
                              },
                            )
                }
              >
                {(failedCount > 0 ||
                  invalidFileCount > 0 ||
                  unrecordableCount > 0 ||
                  partialCount > 0 ||
                  duplicateDoneCount > 0) && (
                  <p>
                    {unrecordableCount > 0
                      ? t(
                          "記録対象が0件のファイルは作成していません。内訳を確認し、列の対応付けやCSVを修正してから再確認してください。",
                        )
                      : failedCount > 0 || invalidFileCount > 0
                        ? t(
                            "取り込めたぶんはそのまま残ります。失敗したファイルだけ、もう一度取り込めます。",
                          )
                        : partialCount > 0
                          ? t(
                              "各ファイルの未一致・無効・数量なしの内訳を確認してください。記録済みの明細は在庫を変更していません。",
                            )
                          : t(
                              "確認済みの操作として別の棚卸し履歴を保存しました。全明細を保存し、現在庫は変更していません。",
                            )}
                  </p>
                )}
              </Banner>
            )}
        </BlockStack>
      </Card>
    </section>
  );
}

interface FileRowViewProps {
  row: FileRow;
  isDuplicateInBatch: boolean;
  isDuplicateOfExisting: boolean;
  onCountedOnChange: (key: string, value: string) => void;
  locationOptions: { label: string; value: string }[];
  onLocationIdChange: (key: string, value: string) => void;
  showCurrentStockMeaning: boolean;
  onCurrentStockMeaningChange: (
    key: string,
    value: CurrentStockMeaning,
  ) => void;
  onDuplicateSourceConfirmationChange: (
    key: string,
    checked: boolean,
  ) => void;
  showStockyListMetadata: boolean;
  stockyListRows: readonly StockyStocktakeListMetadata[];
  selectedStockyListMetadataId: string;
  stockyListSelectionConfirmed: boolean;
  stockyListAssociationMethod:
    "single_file_single_row" | "merchant_selected" | null;
  onStockyListMetadataChange: (key: string, metadataId: string) => void;
  useGeneratedDateAsCountedOn: boolean;
  onUseGeneratedDateChange: (key: string, checked: boolean) => void;
  isDuplicateStockyListMetadata: boolean;
  disabled: boolean;
}

function FileRowView({
  row,
  isDuplicateInBatch,
  isDuplicateOfExisting,
  onCountedOnChange,
  locationOptions,
  onLocationIdChange,
  showCurrentStockMeaning,
  onCurrentStockMeaningChange,
  onDuplicateSourceConfirmationChange,
  showStockyListMetadata,
  stockyListRows,
  selectedStockyListMetadataId,
  stockyListSelectionConfirmed,
  stockyListAssociationMethod,
  onStockyListMetadataChange,
  useGeneratedDateAsCountedOn,
  onUseGeneratedDateChange,
  isDuplicateStockyListMetadata,
  disabled,
}: FileRowViewProps) {
  const isEditable = canInvalidate(row) && row.status !== "checking";
  const selectedStockyListMetadata =
    stockyListRows.find(
      (metadata) => metadata.id === selectedStockyListMetadataId,
    ) ?? null;

  return (
    <Card>
      <BlockStack gap="200">
        <InlineStack align="space-between" blockAlign="center" gap="200">
          {/* ファイル名はマーチャントの実物＝訳さない。 */}
          <Text as="p" fontWeight="medium">
            {row.name}
          </Text>
          <Badge
            tone={
              row.status === "done"
                ? "success"
                : row.status === "partial" ||
                    row.status === "ready_partial" ||
                    row.status === "ready_duplicate" ||
                    row.status === "done_duplicate"
                  ? "attention"
                  : row.status === "ready"
                    ? "success"
                    : row.status === "already"
                      ? "info"
                      : row.status === "failed" ||
                          row.status === "invalid" ||
                          row.status === "unrecordable"
                        ? "critical"
                        : undefined
            }
          >
            {t(STATUS_LABELS[row.status])}
          </Badge>
        </InlineStack>

        {row.status !== "invalid" && (
          <Text as="p" variant="bodySm" tone="subdued">
            {t("{rows} 行", { rows: row.totalRows })}
          </Text>
        )}

        {isEditable && (
          <BlockStack gap="200">
            {showStockyListMetadata && stockyListRows.length > 0 && (
              <Select
                label={t("{name} に対応する Stocky の棚卸し", {
                  name: row.name,
                })}
                options={[
                  {
                    label: t("Stocky一覧から選んでください"),
                    value: "__unselected__",
                    disabled: true,
                  },
                  { label: t("一覧情報を対応しない"), value: "" },
                  ...stockyListRows.map((metadata) => ({
                    label: [
                      metadata.stockyNumber,
                      metadata.name || t("名前なし"),
                      metadata.generatedOn ?? metadata.generatedAtRaw,
                      metadata.locationName || t("拠点不明"),
                    ].join(" — "),
                    value: metadata.id,
                  })),
                ]}
                value={
                  stockyListSelectionConfirmed
                    ? selectedStockyListMetadataId
                    : "__unselected__"
                }
                onChange={(value) => onStockyListMetadataChange(row.key, value)}
                disabled={disabled}
                helpText={
                  stockyListAssociationMethod === "single_file_single_row"
                    ? t("CSVと一覧が1件ずつだったため自動で対応しました。")
                    : t(
                        "CSVにはStockyの棚卸し番号が無いため、ファイル名や並び順では自動判定しません。",
                      )
                }
              />
            )}
            {selectedStockyListMetadata && (
              <Banner
                tone={
                  selectedStockyListMetadata.status
                    .normalize("NFKC")
                    .trim()
                    .toLocaleLowerCase("en-US") === "count in progress"
                    ? "warning"
                    : "info"
                }
                title={t("棚卸しの一覧情報も一緒に保存します")}
              >
                <p>
                  {t(
                    "No.: {number} / Name: {name} / Location: {location} / Generated at: {generatedAt} / Status: {status}",
                    {
                      number: selectedStockyListMetadata.stockyNumber,
                      name: selectedStockyListMetadata.name || t("不明"),
                      location:
                        selectedStockyListMetadata.locationName || t("不明"),
                      generatedAt:
                        selectedStockyListMetadata.generatedAtLocal?.replace(
                          "T",
                          " ",
                        ) ?? selectedStockyListMetadata.generatedAtRaw,
                      status: selectedStockyListMetadata.status || t("不明"),
                    },
                  )}
                </p>
                <p>
                  {t(
                    "Generated at は作成日時です。Statusが途中の棚卸しでも表示されるため、完了日とは扱いません。Nameもコメントではなく棚卸し名として保存します。",
                  )}
                </p>
              </Banner>
            )}
            {selectedStockyListMetadata?.generatedOn && (
              <Checkbox
                label={t(
                  "Stockyの作成日 {date} を、この棚卸しの実施日として使う（自分で確認済み）",
                  { date: selectedStockyListMetadata.generatedOn },
                )}
                checked={useGeneratedDateAsCountedOn}
                onChange={(checked) =>
                  onUseGeneratedDateChange(row.key, checked)
                }
                disabled={disabled}
                helpText={t(
                  "選ばない限り、実施日は不明のままです。作成日時そのものはどちらでも保存されます。",
                )}
              />
            )}
            <TextField
              label={t("{name} の実施日（分かる場合のみ）", { name: row.name })}
              type="date"
              value={row.countedOn}
              onChange={(value) => onCountedOnChange(row.key, value)}
              disabled={disabled}
              autoComplete="off"
              helpText={t(
                "空欄は「不明」として保存し、棚卸し履歴から後で設定できます。",
              )}
            />
            <Select
              label={t("{name} の実施拠点（分かる場合のみ）", {
                name: row.name,
              })}
              options={[
                { label: t("拠点を選択してください"), value: "" },
                ...locationOptions,
              ]}
              value={row.locationId}
              onChange={(value) => onLocationIdChange(row.key, value)}
              disabled={disabled}
              helpText={t(
                "選ばない場合は不明として保存し、棚卸し履歴から後で選べます。",
              )}
            />
          </BlockStack>
        )}

        {isEditable && showCurrentStockMeaning && (
          <ChoiceList
            title={t("{name}: Current Stock はどの在庫数ですか？", {
              name: row.name,
            })}
            choices={[
              {
                label: t("分からない（おすすめ）"),
                helpText: t(
                  "数量を計算せず、CSV の Current Stock と Adjustment をそのまま保存します。",
                ),
                value: "unknown",
              },
              {
                label: t("商品を数えた後の在庫数"),
                helpText: t("棚卸しで実際に数え終わった数量です。"),
                value: "counted",
              },
              {
                label: t("商品を数える前に Stocky に表示されていた在庫数"),
                helpText: t("棚卸しを始める前の帳簿上の数量です。"),
                value: "expected",
              },
            ]}
            selected={
              row.currentStockMeaning === "" ? [] : [row.currentStockMeaning]
            }
            onChange={(selected) => {
              const value = selected[0];
              if (
                value === "unknown" ||
                value === "counted" ||
                value === "expected"
              ) {
                onCurrentStockMeaningChange(row.key, value);
              }
            }}
            disabled={disabled}
          />
        )}

        {isDuplicateOfExisting && (
          <Text as="p" variant="bodySm" tone="caution">
            {t(
              "この日付の棚卸しは既に登録されています。同じファイルを二重に取り込んでいないかご確認ください。",
            )}
          </Text>
        )}
        {isDuplicateInBatch && (
          <Text as="p" variant="bodySm" tone="caution">
            {t("同じ実施日のファイルが他にもあります。")}
          </Text>
        )}
        {isDuplicateStockyListMetadata && (
          <Text as="p" variant="bodySm" tone="critical">
            {t("このStocky一覧行は別のCSVにも選ばれています。")}
          </Text>
        )}

        {row.dryRunResult?.dry_run === true &&
          !row.dryRunResult.already_imported &&
          row.dryRunResult.same_source_file_imports > 0 && (
            <Checkbox
              label={t(
                "同じ内容ですが、別の棚卸しとして保存することを確認しました",
              )}
              checked={row.duplicateSourceConfirmed}
              onChange={(checked) =>
                onDuplicateSourceConfirmationChange(row.key, checked)
              }
              disabled={disabled}
              helpText={t(
                "同じ棚卸しを誤って選び直した場合は選ばないでください。通信エラーの再試行にはこの確認は不要です。",
              )}
            />
          )}

        {row.message && (
          <Text
            as="p"
            variant="bodySm"
            tone={
              row.status === "done" || row.status === "already"
                ? "subdued"
                : row.status === "partial" ||
                    row.status === "ready_partial" ||
                    row.status === "ready_duplicate" ||
                    row.status === "done_duplicate"
                  ? "caution"
                  : row.status === "ready"
                    ? "subdued"
                    : "critical"
            }
          >
            {row.message}
          </Text>
        )}
      </BlockStack>
    </Card>
  );
}
